import { useUnassignedCompanies } from "@vidario/api-client";
import {
  pageRangeLabel,
  type CompanyClassSummary,
  type EmployerGroup,
  type EmployerSort,
} from "@vidario/domain";
import { Button, EmptyState, Input, Pagination, Spinner } from "@vidario/ui";
import { CheckCircle2, Search } from "lucide-react";
import { useEffect, useRef, useState, type ReactElement } from "react";
import { useSearchParams } from "react-router";

import { ClassPicker } from "./ClassPicker";
import { MergeDialog } from "./dialogs";
import { CompanyName } from "./Spellings";
import { assignedMessage, useAssignWithUndo } from "./useAssignWithUndo";

/** How long the search field waits before asking the server. */
const SEARCH_DELAY_MS = 300;
const FIRMS = { one: "Firma", many: "Firmen" };

interface UnassignedTabProps {
  classes: readonly CompanyClassSummary[];
  announce: (text: string) => void;
}

/** „Nicht zugeordnet": every employer without a class, most candidates first. */
export function UnassignedTab({ classes, announce }: UnassignedTabProps): ReactElement {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const sort: EmployerSort = params.get("sort") === "name" ? "name" : "candidates";
  const page = Math.max(1, Number(params.get("page") ?? 1) || 1);

  const update = (next: Record<string, string | null>): void => {
    setParams(
      (current) => {
        const merged = new URLSearchParams(current);
        for (const [k, v] of Object.entries(next)) {
          if (v === null || v === "") merged.delete(k);
          else merged.set(k, v);
        }
        return merged;
      },
      { replace: true },
    );
  };

  // The field answers at once; the URL (and the server) follow after a pause.
  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => {
    if (searchInput === q) return undefined;
    const t = setTimeout(() => update({ q: searchInput, page: null }), SEARCH_DELAY_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `update` only writes the URL
  }, [searchInput, q]);

  const { data, isLoading, isFetching, error, refetch } = useUnassignedCompanies({ q, sort, page });
  const rows = data?.results ?? [];

  // A selection belongs to the rows on screen: another page, search or sort starts afresh.
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const view = `${q}|${sort}|${page}`;
  const [selectionView, setSelectionView] = useState(view);
  if (view !== selectionView) {
    setSelectionView(view);
    setSelected(new Set());
  }
  const selectedRows = rows.filter((r) => selected.has(r.id));
  const [mergeOpen, setMergeOpen] = useState(false);
  const { assign, pending } = useAssignWithUndo(announce);
  const tableRef = useRef<HTMLTableElement>(null);

  const toggle = (id: string): void =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someOnPage = rows.some((r) => selected.has(r.id));

  // After an assignment, focus moves to the row that was below the first
  // assigned one — once the refreshed list no longer shows the assigned rows —
  // or to the caption when nothing is left. Only visible pickers count
  // (phones show the picker under the name instead of in its own column).
  const pendingFocus = useRef<{ leaving: string[]; next: string | null } | null>(null);
  useEffect(() => {
    const pending = pendingFocus.current;
    if (!pending || pending.leaving.some((id) => rows.some((r) => r.id === id))) return;
    const row = pending.next
      ? tableRef.current?.querySelector(`[data-testid="employer-row-${pending.next}"]`)
      : null;
    const picker = [
      ...(row?.querySelectorAll<HTMLInputElement>("input[role='combobox']") ?? []),
    ].find((el) => el.offsetParent !== null);
    (picker ?? tableRef.current?.querySelector<HTMLElement>("caption"))?.focus();
    pendingFocus.current = null;
  }, [rows]);

  const focusAfter = (leaving: readonly EmployerGroup[]): void => {
    const ids = leaving.map((r) => r.id);
    const firstIndex = rows.findIndex((r) => ids.includes(r.id));
    const next =
      rows.slice(firstIndex + 1).find((r) => !ids.includes(r.id)) ??
      rows.find((r) => !ids.includes(r.id)) ??
      null;
    pendingFocus.current = { leaving: ids, next: next?.id ?? null };
  };

  const assignRows = (
    target: readonly EmployerGroup[],
    classId: number,
    className: string,
  ): void => {
    assign({
      rows: target,
      classId,
      message: assignedMessage(target, className),
      onDone: () => {
        setSelected(new Set());
        focusAfter(target);
      },
    });
  };

  const sortButton = (key: EmployerSort, label: string): ReactElement => (
    <button
      type="button"
      onClick={() => update({ sort: key === "candidates" ? null : key, page: null })}
      className="inline-flex cursor-pointer items-center gap-1 uppercase tracking-wider hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
    >
      {label}
      {sort === key && <span aria-hidden="true">{key === "name" ? "↑" : "↓"}</span>}
    </button>
  );

  return (
    <div>
      <div className="mb-4 max-w-sm">
        <Input
          aria-label="Firma suchen"
          placeholder="Firma suchen"
          icon={<Search size={16} />}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>

      {selectedRows.length > 0 && (
        <div
          role="region"
          aria-label="Auswahl"
          className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-border-default bg-neutral-50 px-4 py-2.5 text-sm"
        >
          <span className="font-medium">{selectedRows.length} ausgewählt</span>
          <div className="w-64">
            <ClassPicker
              classes={classes}
              aria-label="Klasse für die Auswahl"
              placeholder="Auswahl zuordnen…"
              onPick={(classId, name) => assignRows(selectedRows, classId, name)}
              disabled={pending}
            />
          </div>
          <Button
            size="sm"
            variant="secondary"
            disabled={selectedRows.length < 2}
            onClick={() => setMergeOpen(true)}
          >
            Zusammenfassen
          </Button>
          <Button size="sm" variant="tertiary" onClick={() => setSelected(new Set())}>
            Auswahl aufheben
          </Button>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center gap-2 px-1 py-6 text-sm text-text-secondary">
          <Spinner size={18} />
          <span>Lädt</span>
        </div>
      )}

      {error && (
        <div role="alert" className="flex items-center gap-3 px-1 py-6 text-sm text-danger-600">
          Firmen konnten nicht geladen werden.
          <Button size="sm" variant="secondary" onClick={() => void refetch()}>
            Erneut versuchen
          </Button>
        </div>
      )}

      {data && data.count === 0 && !q && (
        <EmptyState
          icon={<CheckCircle2 size={28} />}
          title="Alles zugeordnet"
          description="Neue Arbeitgeber aus Kandidaten erscheinen hier automatisch."
        />
      )}
      {data && data.count === 0 && q && (
        <p className="px-1 py-6 text-sm text-text-secondary">Keine Firma passt zu „{q}“.</p>
      )}

      {data && rows.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-lg border border-border-default">
            <table ref={tableRef} className="w-full border-collapse text-sm">
              <caption tabIndex={-1} className="sr-only">
                Nicht zugeordnete Firmen
              </caption>
              <thead>
                <tr className="bg-neutral-50 text-left text-[11px] font-semibold text-text-tertiary">
                  <th scope="col" className="w-10 border-b border-border-default px-4 py-3">
                    <input
                      type="checkbox"
                      aria-label="Alle auf dieser Seite auswählen"
                      checked={allOnPage}
                      ref={(el) => {
                        if (el) el.indeterminate = someOnPage && !allOnPage;
                      }}
                      onChange={() =>
                        setSelected(allOnPage ? new Set() : new Set(rows.map((r) => r.id)))
                      }
                    />
                  </th>
                  <th
                    scope="col"
                    aria-sort={sort === "name" ? "ascending" : "none"}
                    className="border-b border-border-default px-4 py-3"
                  >
                    {sortButton("name", "Firma")}
                  </th>
                  <th
                    scope="col"
                    aria-sort={sort === "candidates" ? "descending" : "none"}
                    className="hidden border-b border-border-default px-4 py-3 text-right sm:table-cell"
                  >
                    {sortButton("candidates", "Kandidaten")}
                  </th>
                  <th
                    scope="col"
                    className="hidden w-64 border-b border-border-default px-4 py-3 uppercase tracking-wider sm:table-cell"
                  >
                    Klasse
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-border-default last:border-b-0"
                    data-testid={`employer-row-${row.id}`}
                  >
                    <td className="px-4 py-2.5 align-middle">
                      <input
                        type="checkbox"
                        aria-label={`„${row.name}“ auswählen`}
                        checked={selected.has(row.id)}
                        onChange={() => toggle(row.id)}
                      />
                    </td>
                    <td className="max-w-80 px-4 py-2.5 align-middle">
                      <CompanyName group={row} />
                      {/* Phones: count and picker under the name, so the main action stays on screen. */}
                      <div className="mt-1.5 flex flex-col gap-2 sm:hidden">
                        <span className="text-xs text-text-tertiary">
                          {row.candidateCount}{" "}
                          {row.candidateCount === 1 ? "Kandidat" : "Kandidaten"}
                        </span>
                        <ClassPicker
                          classes={classes}
                          aria-label={`Klasse für „${row.name}“`}
                          onPick={(classId, name) => assignRows([row], classId, name)}
                          disabled={pending}
                          openOnFocus={false}
                        />
                      </div>
                    </td>
                    <td className="hidden px-4 py-2.5 text-right align-middle tabular-nums text-text-secondary sm:table-cell">
                      {row.candidateCount}
                    </td>
                    <td className="hidden px-4 py-2 align-middle sm:table-cell">
                      <ClassPicker
                        classes={classes}
                        aria-label={`Klasse für „${row.name}“`}
                        onPick={(classId, name) => assignRows([row], classId, name)}
                        disabled={pending}
                        openOnFocus={false}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            className="mt-4"
            page={data.page}
            pageCount={data.pageCount}
            onPageChange={(next) => update({ page: next === 1 ? null : String(next) })}
            summary={
              <span className="inline-flex items-center gap-2">
                {pageRangeLabel(data.page, data.pageSize, data.count, FIRMS)}
                {isFetching && <Spinner label="Lädt" />}
              </span>
            }
          />
        </>
      )}

      {mergeOpen && (
        <MergeDialog
          open={mergeOpen}
          onOpenChange={setMergeOpen}
          rows={selectedRows}
          classes={classes}
          onMerged={(text) => {
            setSelected(new Set());
            announce(`${text}.`);
            // The merge button is gone with the selection; go back to the list.
            requestAnimationFrame(() =>
              tableRef.current?.querySelector<HTMLElement>("caption")?.focus(),
            );
          }}
        />
      )}
    </div>
  );
}
